import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

// File attachment from MaintenanceRequestFiles model
export interface IMaintenanceFile {
  id: number;
  maintenanceRequestId: number;
  fileUrl: string;
}

// Types matching the web app interfaces
export interface IMaintenanceListResponse {
  id: number;
  maintenanceRequestId: number;
  maintenanceStatus: string;
  maintenanceLocation: string;
  maintenanceProblem?: string;
  maintenanceCatagoryName?: string; // Note: keeping the typo to match API
  maintenanceCategoryName?: string;
  maintenanceSubCategoryName?: string; // Added for Yardi
  maintenancePriority: string;
  maintenanceDescription: string;
  createdOn: string;
  updatedOn?: string;
  completedOn?: string;
  assignedToCompanyEmployeeId?: number;
  permissionToEnter?: number | boolean;
  hasAttachments?: boolean; // Added for Yardi
  files?: IMaintenanceFile[]; // File attachments from backend
}

export interface IMaintenanceSubCategory { id: number | string; name: string }
export interface IMaintenanceCategory { id: number | string; name: string; subCategories?: IMaintenanceSubCategory[] }
export interface IMaintenanceLocation { id: number | string; name: string; type?: string; isResidentPortalEnabled?: string; n_problems?: number }
export interface IMaintenancePriority { id: number | string; name: string; code?: string }
export interface IMaintenanceProblem { id: number | string; name: string; categoryId?: number | string; locations?: Array<string>; isResidentPortalEnabled?: string }

export interface IMaintenanceInfoResponse {
  categories?: IMaintenanceCategory[];
  locations?: IMaintenanceLocation[];
  priorities?: IMaintenancePriority[];
  problems?: IMaintenanceProblem[];
}

export interface IMaintenanceResponse<T> {
  success: boolean;
  response: T;
  message?: string;
}

// RTK Query API slice for maintenance
export const maintenanceApi = createApi({
  reducerPath: 'maintenanceApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['MaintenanceList', 'MaintenanceInfo'],
  endpoints: (builder) => ({
    // Get maintenance info (categories, priorities, etc.)
    getMaintenanceInfo: builder.query<IMaintenanceResponse<IMaintenanceInfoResponse>, void>({
      query: () => ({
        url: '/api/v2/maintenance/info',
        method: 'GET',
      }),
      providesTags: ['MaintenanceInfo'],
    }),

    // Get maintenance list
    getMaintenanceList: builder.query<IMaintenanceResponse<IMaintenanceListResponse[]>, void>({
      query: () => ({
        url: '/api/v2/maintenance/list',
        method: 'GET',
      }),
      providesTags: ['MaintenanceList'],
      // Survive screen unmount/remount cycles so navigating back doesn't
      // re-trigger the slow list fetch. Pull-to-refresh and ticket creation
      // (which invalidates the tag) still get fresh data.
      keepUnusedDataFor: 300,
    }),

    // Create maintenance ticket
    postMaintenanceTicket: builder.mutation<IMaintenanceResponse<any>, FormData>({
      query: (formData) => ({
        url: '/api/v2/maintenance/create',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['MaintenanceList'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetMaintenanceInfoQuery,
  useLazyGetMaintenanceInfoQuery,
  useGetMaintenanceListQuery,
  useLazyGetMaintenanceListQuery,
  usePostMaintenanceTicketMutation,
} = maintenanceApi;
